import Phaser from 'phaser';
import { COLORS, DEPTHS, FONTS, GAME_WIDTH, MENU_BAR, toHexColor } from '../config/gameConfig';
import { formatFictionalTime, FICTION_START_MINUTES } from '../systems/Clock';
import type { EventBus } from '../systems/EventBus';
import { COPY } from '../content/copy';
import { AudioManager } from '../systems/AudioManager';

const ICON_SIZE = 14;

export class MenuBar extends Phaser.GameObjects.Container {
  private readonly clockText: Phaser.GameObjects.Text;
  private readonly unreadText: Phaser.GameObjects.Text;
  private readonly slackText: Phaser.GameObjects.Text;
  private readonly dsoText: Phaser.GameObjects.Text;
  private readonly scoreText: Phaser.GameObjects.Text;
  private readonly muteIcon: Phaser.GameObjects.Image;
  private readonly bus: EventBus;
  private readonly onTick = (p: { fictionalMinutes: number }) => {
    this.clockText.setText(formatFictionalTime(p.fictionalMinutes));
  };
  private readonly onUnread = (p: { count: number }) => {
    this.unreadText.setText(String(p.count));
  };
  private readonly onSlackUnread = (p: { count: number }) => {
    this.slackText.setText(String(p.count));
  };
  private readonly onDso = (p: { dso: number }) => {
    this.dsoText.setText(String(p.dso));
  };
  private readonly onScore = (p: { score: number; delta: number }) => {
    this.scoreText.setText(String(p.score));
    this.scoreText.setColor(toHexColor(p.score >= 0 ? COLORS.success : COLORS.warning));
    if (p.delta !== 0) this.spawnDeltaTicker(p.delta);
  };

  private spawnDeltaTicker(delta: number): void {
    const startX = this.scoreText.x + this.scoreText.width + 8;
    const startY = this.scoreText.y;
    const positive = delta > 0;
    const text = `${positive ? '+' : ''}${delta}`;
    const ghost = this.scene.add.text(startX, startY, text, {
      fontFamily: FONTS.ui,
      fontSize: '14px',
      color: toHexColor(positive ? COLORS.success : COLORS.warning),
      fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(DEPTHS.menuBar + 1);
    this.add(ghost);
    this.scene.tweens.add({
      targets: ghost,
      y: startY - 14,
      alpha: 0,
      duration: 900,
      ease: 'Cubic.easeOut',
      onComplete: () => ghost.destroy(),
    });
    this.scene.tweens.add({
      targets: this.scoreText,
      scale: 1.18,
      duration: 110,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
  }

  constructor(scene: Phaser.Scene, bus: EventBus) {
    super(scene, 0, 0);
    scene.add.existing(this);
    this.setDepth(DEPTHS.menuBar);
    this.bus = bus;

    const bg = scene.add.rectangle(0, 0, GAME_WIDTH, MENU_BAR.height, COLORS.menuBar).setOrigin(0, 0);
    this.add(bg);

    const divider = scene.add.rectangle(0, MENU_BAR.height - 1, GAME_WIDTH, 1, COLORS.menuBarDivider).setOrigin(0, 0);
    this.add(divider);

    const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: FONTS.ui,
      fontSize: '13px',
      color: toHexColor(COLORS.menuBarText),
    };

    const cy = MENU_BAR.height / 2;
    const ICON_GAP = 6;

    // Clock — icon + text
    const clockIcon = this.makeIcon(scene, 'clock', MENU_BAR.clockX, cy);
    this.add(clockIcon);
    this.clockText = scene.add.text(
      MENU_BAR.clockX + ICON_SIZE + ICON_GAP,
      cy,
      formatFictionalTime(FICTION_START_MINUTES),
      textStyle,
    ).setOrigin(0, 0.5);
    this.add(this.clockText);

    // Unread — icon + count
    const unreadIcon = this.makeIcon(scene, 'inbox', MENU_BAR.unreadX, cy);
    this.add(unreadIcon);
    this.unreadText = scene.add.text(
      MENU_BAR.unreadX + ICON_SIZE + ICON_GAP,
      cy,
      '0',
      textStyle,
    ).setOrigin(0, 0.5);
    this.add(this.unreadText);

    // Slack — icon + count
    const slackIcon = this.makeIcon(scene, 'message-circle', MENU_BAR.slackX, cy);
    this.add(slackIcon);
    this.slackText = scene.add.text(
      MENU_BAR.slackX + ICON_SIZE + ICON_GAP,
      cy,
      '0',
      textStyle,
    ).setOrigin(0, 0.5);
    this.add(this.slackText);

    // DSO — icon + value
    const dsoIcon = this.makeIcon(scene, 'trending-down', MENU_BAR.dsoX, cy);
    this.add(dsoIcon);
    this.dsoText = scene.add.text(
      MENU_BAR.dsoX + ICON_SIZE + ICON_GAP,
      cy,
      COPY.dso.menuPlaceholder,
      textStyle,
    ).setOrigin(0, 0.5);
    this.add(this.dsoText);

    // Score — icon + live total
    const scoreIcon = this.makeIcon(scene, 'trending-up', MENU_BAR.scoreX, cy);
    this.add(scoreIcon);
    this.scoreText = scene.add.text(
      MENU_BAR.scoreX + ICON_SIZE + ICON_GAP,
      cy,
      '0',
      { ...textStyle, color: toHexColor(COLORS.success) },
    ).setOrigin(0, 0.5);
    this.add(this.scoreText);

    // Mute toggle — icon button
    this.muteIcon = this.makeIcon(
      scene,
      AudioManager.getInstance().getMuted() ? 'volume-x' : 'volume-2',
      MENU_BAR.muteX,
      cy,
    );
    this.muteIcon.setInteractive({ useHandCursor: true });
    this.muteIcon.on('pointerdown', () => {
      const muted = AudioManager.getInstance().toggleMute();
      this.muteIcon.setTexture(`icon:${muted ? 'volume-x' : 'volume-2'}`);
    });
    this.add(this.muteIcon);

    // Settings — gear icon at far right (top-right of the desktop).
    const settingsIcon = this.makeIcon(scene, 'settings', MENU_BAR.settingsX, cy);
    settingsIcon.setInteractive({ useHandCursor: true });
    settingsIcon.on('pointerdown', () => {
      this.bus.emit('settings:open-requested', {});
    });
    this.add(settingsIcon);

    // ESC: pause hint with pause icon
    const pauseIcon = this.makeIcon(scene, 'pause', MENU_BAR.pauseHintX, cy);
    pauseIcon.setAlpha(0.6);
    this.add(pauseIcon);
    const pauseHint = scene.add.text(
      MENU_BAR.pauseHintX + ICON_SIZE + ICON_GAP,
      cy,
      'ESC',
      { ...textStyle, color: toHexColor(COLORS.textMuted), fontSize: '11px' },
    ).setOrigin(0, 0.5);
    this.add(pauseHint);

    this.bus.on('clock:tick', this.onTick);
    this.bus.on('inbox:unread-changed', this.onUnread);
    this.bus.on('slack:unread-changed', this.onSlackUnread);
    this.bus.on('dso:opened', this.onDso);
    this.bus.on('score:changed', this.onScore);

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  private makeIcon(scene: Phaser.Scene, name: string, x: number, y: number): Phaser.GameObjects.Image {
    const img = scene.add.image(x + ICON_SIZE / 2, y, `icon:${name}`);
    img.setDisplaySize(ICON_SIZE, ICON_SIZE);
    img.setOrigin(0.5, 0.5);
    return img;
  }

  private teardown(): void {
    this.bus.off('clock:tick', this.onTick);
    this.bus.off('inbox:unread-changed', this.onUnread);
    this.bus.off('slack:unread-changed', this.onSlackUnread);
    this.bus.off('dso:opened', this.onDso);
    this.bus.off('score:changed', this.onScore);
  }
}
